<template>
  <div v-if="!isLoading && hasData" class="left-behind">
    <table :class="{compressed: !isSiteAllowedToEdit}">
      <thead>
        <tr>
          <th v-for="(header, i) in headers">
            <div class="header" @click="sortIndicators()">
              <span>{{ $t(`leftBehind.tableTitle.${header}`) }}</span>
              <v-icon
                v-if="i === 0"
                :class="{top: sortOrder === 'desc' }"
                class="icon-collape"
                stroke-width="1.5">
                down-arrow
              </v-icon>
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(indicator, i) in indicators" :key="i">
          <td :class="{left: !isSiteAllowedToEdit, isUpdated: isUpdated(indicator) }">
            <inline-smart-combobox
              v-if="isSiteAllowedToEdit"
              :class="{error: errorsDetail[i].customer && hasClickedOnSend}"
              :items="getCustomersList"
              :value="indicator.customer && indicator.customer.label ? indicator.customer.label : ''"
              class="customer-combobox"
              placeholder="Choisissez votre client"
              @reset="onCustomerReset(i)"
              @select="onCustomerSelected(i, $event)" />
            <span v-else> {{ indicator.customer && indicator.customer.label ? indicator.customer.label : '' }} </span>
          </td>
          <td :class="{isUpdated: isUpdated(indicator)}">
            <inline-smart-combobox
              v-if="isSiteAllowedToEdit"
              :class="{error: errorsDetail[i].sector && hasClickedOnSend}"
              :disabled="siteHasOnlyOneSector"
              :items="getSectorsList"
              :value="indicator.sector && indicator.sector.label ? indicator.sector.label : ''"
              placeholder="Choisissez votre secteur"
              @reset="onSectorReset(i)"
              @select="onSectorSelected(i, $event)" />
            <span v-else> {{ indicator.sector && indicator.sector.label ? indicator.sector.label : '' }} </span>
          </td>
          <td :class="{isUpdated: isUpdated(indicator) }">
            <input
              v-if="isSiteAllowedToEdit"
              :class="{error: errorsDetail[i].toSendShippingUnit && hasClickedOnSend}"
              :value="indicator.toSendShippingUnit"
              type="number"
              @input="onCellChange(i, 'toSendShippingUnit', $event.target.valueAsNumber)">
            <span v-else> {{ indicator.toSendShippingUnit }} </span>
          </td>
          <td :class="{isUpdated: isUpdated(indicator) }">
            <input
              :class="{error: errorsDetail[i].notDeliveredShippingUnit && hasClickedOnSend}"
              :disabled="!indicator.toSendShippingUnit"
              :value="indicator.notDeliveredShippingUnit"
              type="number"
              @input="onCellChange(i, 'notDeliveredShippingUnit', $event.target.valueAsNumber)">
            <v-icon v-if="isSiteAllowedToEdit"
                    class="icon-delete-row"
                    color="white"
                    font-size="2.5em"
                    @click="deleteRow(i)">
              trash
            </v-icon>
          </td>
        </tr>
        <tr v-if="isSiteAllowedToEdit" :key="indicators.length + 1" @click="addRow">
          <td class="add-row">
            <v-icon class="icon-angle-arrow" color="white" font-size="2.5em">add-row</v-icon>
            <span class="add-row-label">Ajouter une ligne</span>
          </td>
          <td />
          <td />
          <td />
        </tr>
      </tbody>
    </table>
    <floating-button class="floating-validate"
                     color="green"
                     icon="accept"
                     text="Valider"
                     @validate="sendDatas()" />
  </div>
  <loader v-else-if="isLoading" color="#DA4453" />
  <no-data v-else />
</template>

<script>
  import InlineSmartCombobox from '@/components/left-behind/InlineSmartCombobox'
  import indicatorsService from '@/services/indicatorsService'
  import { debounced } from '@/utils/decorators'
  import { sendErrorToast } from '@/utils/utils'
  import { mapGetters } from 'vuex'


  const TABLE_HEADERS = ['customer', 'sector', 'toSendShippingUnit', 'notDeliveredShippingUnit']

  export default {
    name: 'LeftBehind',
    components: { InlineSmartCombobox },
    data() {
      return {
        headers: TABLE_HEADERS,
        isLoading: false,
        indicators: [],
        sortOrder: 'desc',
        hasClickedOnSend: false
      }
    },

    watch: {
      'site'(newValue, oldValue) {
        if (this.isValidContext) this.fetchData()
      },

      'period'(newValue, oldValue) {
        if (this.isValidContext) this.fetchData()
      }
    },

    created() {
      this.fetchData()
    },

    computed: {
      ...mapGetters('context', ['site', 'period', 'isValidContext']),
      ...mapGetters('referential', ['customers']),

      hasData() {
        return this.indicators && this.indicators.length > 0
      },

      getCustomersList() {
        return this.customers ? this.customers.map(customer => customer.label) : []
      },

      getSectorsList() {
        return this.site && this.site.sectors ? this.site.sectors.map(sector => sector.label) : []
      },

      siteHasOnlyOneSector() {
        return this.site && this.site.sectors && this.site.sectors.length === 1
      },

      isSiteAllowedToEdit() {
        const isSiteSectorPGCNAL = this.site && this.site.sectors.some(sector => sector.label === 'PGC' || sector.label === 'NAL')
        return !isSiteSectorPGCNAL || this.site.isFullAccess
      },

      errorsDetail() {
        return this.indicators.map((indicator, i) => {
          const duplicatedCustomer = this.indicators.some((uindicator, j) => {
            if (indicator.customer && indicator.sector && uindicator.customer && uindicator.sector) {
              return i !== j && indicator.sector.sid === uindicator.sector.sid && indicator.customer.sid === uindicator.customer.sid
            } else return false
          })

          if (duplicatedCustomer) sendErrorToast(this.$t('leftBehind.errors.duplicatedCustomer'))

          return {
            customer: (!indicator.customer || duplicatedCustomer) && indicator.updated,
            sector: !indicator.sector && indicator.updated,
            toSendShippingUnit: !isFinite(indicator.toSendShippingUnit) && indicator.updated,
            notDeliveredShippingUnit: !isFinite(indicator.notDeliveredShippingUnit) && indicator.updated
          }
        })
      },

      hasErrors() {
        return this.errorsDetail.some(row => Object.keys(row).some(key => row[key]))
      }
    },

    methods: {
      onCustomerSelected(rowId, customerId) {
        this.indicators[rowId].customer = this.customers[customerId]
        this.indicators[rowId].updated = true
      },

      onCustomerReset(rowId) {
        this.indicators[rowId].customer = null
      },

      onSectorSelected(rowId, sectorId) {
        this.indicators[rowId].sector = this.site.sectors[sectorId]
        this.indicators[rowId].updated = true
      },

      onSectorReset(rowId) {
        this.indicators[rowId].sector = null
      },

      onCellChange(rowId, key, val) {
        const initialValue = this.indicators[rowId][key]

        this.indicators[rowId][key] = val
        if (val < 0) {
          this.indicators[rowId][key] = initialValue
          sendErrorToast('leftBehind.errors.valInfZero')
        }
        this.indicators[rowId].updated = Number.isInteger(val)
        this.indicators[rowId].userCode = this.$userInfos && this.$userInfos.employeeNumber ? this.$userInfos.employeeNumber : ''
      },

      isUpdated(indicator) {
        return indicator.lastUpdatedDate !== null || indicator.updated && !this.isSiteAllowedToEdit
      },

      sortIndicators() {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
        if (this.sortOrder === 'desc') this.indicators = this.indicators.reverse()
        else this.indicators = this.indicators.sort((a, b) => {
          if (!a.customer && !b.customer) return 0
          if (!a.customer) return 1
          if (!b.customer) return -1
          const idA = a.customer ? parseInt(a.customer.label.split(' ')[0]) : ''
          const idB = b.customer ? parseInt(b.customer.label.split(' ')[0]) : ''
          return idA > idB ? 1 : idA === idB ? 0 : -1
        })
      },

      initIndicators() {
        this.indicators = []
        for (let i = 0; i < 6; i++) this.addRow()
      },

      addRow() {
        this.indicators.push(this.emptyRow())
      },

      deleteRow(rowId) {
        rowId === 0 ? this.indicators.splice(rowId, 1, this.emptyRow()) : this.indicators.splice(rowId, 1)
      },

      emptyRow() {
        const sector = this.site.sectors.length === 1 ? this.site.sectors[0] : null
        return {
          customer: null,
          sector,
          toSendShippingUnit: null,
          site: this.site,
          notDeliveredShippingUnit: null,
          userCode:'',
          updated: false
        }
      },

      @debounced(1000)
      async sendDatas() {
        this.isLoading = true
        this.hasClickedOnSend = true
        if (!this.hasErrors) {

          // This hack was added due to work use cases problems with database
          // (fields was sent with null value and 0 was needed instead)
          // I had to override values from null to 0
          // In case of the site is not allowed to Edit (PGC && PROXY e.g.)
          // we update the "updated" boolean to true for all fields.
          this.indicators.forEach(indicator => {
            if (indicator.toSendShippingUnit === null) indicator.toSendShippingUnit = 0
            if (indicator.notDeliveredShippingUnit === null) indicator.notDeliveredShippingUnit = 0
            if(indicator.updated) {
              indicator.userCode = this.$userInfos && this.$userInfos.employeeNumber ? this.$userInfos.employeeNumber : ''
            }
             if (!this.isSiteAllowedToEdit) {
             indicator.updated = true
             indicator.userCode = this.$userInfos && this.$userInfos.employeeNumber ? this.$userInfos.employeeNumber : ''
            }
          })

          await indicatorsService.setLeftBehindIndicators({
            site: this.site,
            date: this.period,
            indicators: this.indicators
          })
          this.isLoading = false
          this.hasClickedOnSend = false
        } else sendErrorToast(this.$t('leftBehind.errors.inputError'))

      },

      async fetchData() {
        this.isLoading = true
        this.indicators = await indicatorsService.getLeftBehindIndicators({
          site: this.site,
          date: this.period
        })
        this.sortOrder = 'desc'
        this.sortIndicators()
        if ((!this.indicators || this.indicators.length === 0) && this.isSiteAllowedToEdit) this.initIndicators()
        this.isLoading = false
      }
    }
  }
</script>

<style lang="scss" scoped>
  .left-behind, .v-loader, .noData {
    padding: 0 4rem;
    height: calc(77% - 40px);
    overflow-y: auto;
    margin-top: 2rem;
    background-color: $app-background;

    table {
      border-collapse: collapse;
      font-family: $secondary-font;
      border: 1px solid #E7E8EA;
      margin-bottom: 1.5rem;
      box-shadow: 5px 5px 10px rgba(0, 0, 0, 0.15);

      thead {
        th {
          background: $light-red;
          font-weight: normal;
          color: white;
          position: sticky;
          top: -1px;
          font-size: 14px;
          height: 40px;
          padding: 0 15px;
          z-index: 1;

          .header { cursor: pointer }

          .icon-collape {
            margin-left: 1rem;
            transition: 0.3s all ease-in-out;

            &.top {
              transform: rotate(180deg);
              transition: 0.3s all ease-in-out;
            }
          }
        }
      }

      tbody {
        position: relative;
        background-color: white;

        td {
          min-height: 39px;
          padding: 10px 15px;
          text-align: center;
          color: #707070;
          font-size: 13px;
          position: relative;
          background-color: white;

          &.left { text-align: left }

          .error { border: 2px solid red; }
        }

        .isUpdated{
          background-color: #e1dbdb;
        }

        .icon-delete-row {
          padding: 0.7rem;
          background-color: $light-red;
          border-radius: 50%;
          height: 100%;
          color: white;
          position: absolute;
          right: -50px;
          cursor: pointer;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
          transition: all 0.3s cubic-bezier(.25, .8, .25, 1);

          &:hover { box-shadow: 0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22); }
        }

        .inline-smart-combobox {
          min-width: 50px;
          padding: 6px;

          &.customer-combobox {
            min-width: 260px;
          }
        }

        input {
          width: 60px;
          height: 36px;
          border: 2px solid #D8D7D9;
          color: #707070;
          text-align: center;
          font-size: 13px;
        }

        .add-row {
          cursor: pointer;
          font-weight: bold;
          text-align: left;

          .add-row-label {
            font-size: 14px;
          }

          .icon-angle-arrow {
            margin-right: 1rem;
          }
        }

        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
      }

      &.compressed {
        td { padding: 5px 15px; }

        input {
          height: 24px;
          width: 45px;
        }
      }
    }
  }

  .left-behind::-webkit-scrollbar-track {
    -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.1);
    background-color: #F5F5F5;
    border-radius: 10px;
  }

  .left-behind::-webkit-scrollbar {
    width: 10px;
    background-color: #F5F5F5;
  }

  .left-behind::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background-color: #C9C4C4;
  }
</style>
